import type { SegmentConditionPayload } from "src/services/SegmentAPIs";
import type {
  ConditionCategory,
  EmptyCondition,
  FilledCondition,
  OrderComparison,
  PeriodUnit,
  SegmentCondition,
  SegmentConditionDraft,
  SegmentRules,
} from "./CreateSegment.Constants";

const CONDITION_DEFAULTS: Record<ConditionCategory, SegmentConditionDraft> = {
  list: { category: "list", membership: "in", listName: null },
  location: { category: "location", field: "city", operator: "isOneOf", values: [] },
  orderActivity: {
    category: "orderActivity",
    action: "placedOrder",
    comparison: "atLeast",
    count: 1,
    periodValue: 30,
    periodUnit: "days",
  },
  signupDate: { category: "signupDate", operator: "before", date: null },
};

const COMPARISON_PAYLOAD: Record<OrderComparison, "at_least" | "at_most" | "exactly"> = {
  atLeast: "at_least",
  atMost: "at_most",
  exactly: "exactly",
};

const DAYS_PER_UNIT: Record<PeriodUnit, number> = { days: 1, weeks: 7, months: 30 };

export const createEmptyCondition = (id: number): EmptyCondition => ({ id, category: null });

export const withConditionId = (draft: SegmentConditionDraft, id: number): FilledCondition => ({
  ...draft,
  id,
});

export const createCondition = (category: ConditionCategory, id: number): FilledCondition =>
  withConditionId(CONDITION_DEFAULTS[category], id);

export const isFilledCondition = (condition: SegmentCondition): condition is FilledCondition =>
  condition.category !== null;

export const isConditionComplete = (condition: SegmentCondition): boolean => {
  switch (condition.category) {
    case "list":
      return Boolean(condition.listName);
    case "location":
      return condition.values.length > 0;
    case "orderActivity":
      return (
        (condition.action === "notPlacedOrder" || Boolean(condition.count)) &&
        Boolean(condition.periodValue)
      );
    case "signupDate":
      return Boolean(condition.date);
    default:
      return false;
  }
};

export const areRulesComplete = ({ conditions }: SegmentRules) =>
  conditions.length > 0 && conditions.every(isConditionComplete);

export const parsePositiveInteger = (value: string): number | null => {
  const parsed = Number.parseInt(value.replace(/\D/g, ""), 10);
  return Number.isNaN(parsed) || parsed < 1 ? null : parsed;
};

const toConditionPayload = (condition: FilledCondition): SegmentConditionPayload => {
  switch (condition.category) {
    case "list":
      return {
        type: "list_membership",
        list_name: condition.listName ?? "",
        operator: condition.membership === "notIn" ? "not_in" : "in",
      };
    case "location":
      return {
        type: "location",
        field: condition.field,
        operator: condition.operator === "isNotOneOf" ? "is_not_one_of" : "is_one_of",
        values: condition.values,
      };
    case "orderActivity": {
      // The API only knows "placed_order"; "has not ordered" is exactly zero orders.
      const placedOrder = condition.action === "placedOrder";
      return {
        type: "shopping_activity",
        action: "placed_order",
        comparator: placedOrder ? COMPARISON_PAYLOAD[condition.comparison] : "exactly",
        count: placedOrder ? condition.count ?? 0 : 0,
        window_days: (condition.periodValue ?? 0) * DAYS_PER_UNIT[condition.periodUnit],
      };
    }
    case "signupDate":
      return { type: "signup_date", operator: condition.operator, date: condition.date ?? "" };
  }
};

/** Conditions in API form; every condition after the first carries how it joins the previous one. */
export const buildSegmentConditions = ({
  conditions,
  operators,
}: SegmentRules): SegmentConditionPayload[] =>
  conditions.filter(isFilledCondition).map((condition, index) => {
    const payload = toConditionPayload(condition);
    return index === 0 ? payload : { ...payload, join: operators[index - 1] ?? "and" };
  });
